import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { adminApi, apiError, setToken } from "@/lib/api";
import { Field, FormSummary, RequiredLegend, focusFirstError, clean } from "@/components/landing/Field";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");
  const [invalid, setInvalid] = useState("");
  const [f, setF] = useState({ new_password: "", confirm_password: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    adminApi.get(`/password/reset/${token}`)
      .then(() => setState("ready"))
      .catch((e) => { setInvalid(apiError(e)); setState("invalid"); });
  }, [token]);

  const submit = async (ev) => {
    ev.preventDefault();
    const e = clean({
      new_password: f.new_password.length < 10 && "Le nouveau mot de passe doit comporter au moins 10 caractères.",
      confirm_password: (!f.confirm_password || f.confirm_password !== f.new_password) && "La confirmation ne correspond pas au nouveau mot de passe.",
    });
    setErrors(e);
    if (Object.keys(e).length) return focusFirstError(ev.currentTarget);
    setLoading(true);
    try {
      await adminApi.post("/password/reset", { token, ...f });
      setToken(null);
      navigate("/admin?reinitialise=1", { replace: true });
    } catch (ex) {
      setInvalid(apiError(ex));
      setState("invalid");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ice-soft flex items-center justify-center px-5">
      <div className="order-card w-full max-w-sm p-8 flex flex-col gap-4" data-testid="reset-password-page">
        <span className="font-display text-2xl font-extrabold text-slate-900">Nouveau mot de passe</span>
        {state === "checking" && <Loader2 className="w-6 h-6 animate-spin text-slate-400" />}
        {state === "invalid" && (
          <>
            <div data-testid="reset-password-invalid" className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">{invalid}</div>
            <a href="/admin" data-testid="reset-password-login-link" className="text-sm text-sky-700 hover:underline">Retour à la connexion</a>
          </>
        )}
        {state === "ready" && (
          <form onSubmit={submit} noValidate data-testid="reset-password-form" className="flex flex-col gap-4">
            <RequiredLegend testId="reset-password-legend" />
            {[["new_password", "Nouveau mot de passe (10 caractères minimum)"], ["confirm_password", "Confirmer le nouveau mot de passe"]].map(([k, label]) => (
              <Field key={k} id={`reset-${k}`} label={label} required error={errors[k]}>
                <input id={`reset-${k}`} type="password" autoComplete="new-password" data-testid={`reset-${k}-input`} value={f[k]} aria-invalid={!!errors[k]}
                  onChange={(e) => { setF({ ...f, [k]: e.target.value }); setErrors({}); }} className="field-input" />
              </Field>
            ))}
            <FormSummary errors={errors} testId="reset-password-summary" />
            <button type="submit" disabled={loading} data-testid="reset-password-submit-button" className="btn-heat">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enregistrer le mot de passe"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
